import { useState } from "react";
import { Send, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CHANGE_TYPES } from "./constants";

interface CreateRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (type: string, changes: string[]) => Promise<void>;
}

export function CreateRequestDialog({
  open,
  onOpenChange,
  onSubmit,
}: CreateRequestDialogProps) {
  const [changeType, setChangeType] = useState<string>("deliverables");
  const [changes, setChanges] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!changes.trim()) return;

    setSubmitting(true);
    try {
      await onSubmit(
        changeType,
        changes
          .split("\n")
          .map((c) => c.trim())
          .filter(Boolean)
      );

      setChanges("");
      setChangeType("deliverables");
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Change Request</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Change Type</Label>
            <Select value={changeType} onValueChange={setChangeType}>
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHANGE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Describe Your Changes</Label>
            <Textarea
              className="mt-1.5 min-h-[150px] resize-none"
              placeholder="Enter each change on a new line..."
              value={changes}
              onChange={(e) => setChanges(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              Enter each requested change on a new line
            </p>
          </div>

          <div className="flex items-start gap-3 p-4 bg-amber-500/5 border border-amber-500/10 rounded-xl">
            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              Change requests will be reviewed by our team. Approved changes may
              affect project timeline and budget, which will be outlined in a
              change order for your approval.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !changes.trim()}>
            {submitting ? (
              "Submitting..."
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Request
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

